/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import ComboBoxField from 'components/ComboBoxField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { makeStyles } from 'tss-react/mui';
import type { HTMLAttributes } from 'react';
import type { TSearchFilterRow } from 'types/search';
import { TSchema } from 'types/items';
import { TParsedFieldWithFieldName } from '../utilities';

export interface AdvSearchDialogProps extends HTMLAttributes<HTMLDivElement> {
  disablePagination: boolean;
  forceRefresh: boolean;
  handleCancel: () => void;
  handleSave: () => void;
  open: boolean;
  fields: TParsedFieldWithFieldName[];
  schema: TSchema<any>;
  searchFilters: TSearchFilterRow[];
  setDisablePagination: (disablePagination: boolean) => void;
  setForceRefresh: (forceRefresh: boolean) => void;
  setSearchFilters: (searchFilters: TSearchFilterRow[]) => void;
}

export type AdvSearchDialogComponent = FC<AdvSearchDialogProps>;

const useStyles = makeStyles()(() => ({
  anyOfField: {
    marginBottom: 10,
  },
  iconButton: {
    margin: 2,
  },
  isAnyOfField: {
    marginTop: 5,
  },
  tabComboBox: {
    margin: 2,
    marginTop: 5,
    width: '100%',
  },
  textField: {
    margin: 2,
    marginTop: 5,
    width: '100%',
  },
}));

const AdvSearchDialog: AdvSearchDialogComponent = ({
  disablePagination,
  fields,
  forceRefresh,
  handleCancel,
  handleSave,
  open,
  schema,
  searchFilters,
  setDisablePagination,
  setForceRefresh,
  setSearchFilters,
}) => {
  const { classes } = useStyles();

  // Handlers
  const handleInputChange = (idx: number, event: React.ChangeEvent<any>) => {
    // Update the specified row in the searchFilters state with the index of idx
    // with the value specified in event.target.value
    const { name, value } = event.target;
    const updatedSearchFilters = [...searchFilters];
    updatedSearchFilters[idx] = {
      ...updatedSearchFilters[idx],
      [name]: value,
    };
    setSearchFilters(updatedSearchFilters);
  };
  const handleAnyOfInputChange = (
    idx: number,
    event: React.ChangeEvent<any>,
    values: any[]
  ) => {
    // Update the specified row in the searchFilters state with the index of idx
    // with the value specified in event.target.value.  If this is a freeSolo
    // AutoComplete then we are getting an aray of strings as values here and we
    // can just apply it as-is.  However, if it is not a freeSolo then we need to
    // map out the value property of the specified values object, which is a key
    // value pair, and apply it to the values array.
    const updatedSearchFilters = [...searchFilters];

    const updatedValues: any[] = [];
    values.forEach((value) => {
      if (typeof value === 'object') {
        updatedValues.push(value.id);
      } else {
        updatedValues.push(value);
      }
    });

    updatedSearchFilters[idx] = {
      ...updatedSearchFilters[idx],
      values: updatedValues,
    };

    setSearchFilters(updatedSearchFilters);
  };

  const renderAnyOfEnumTags = (
    value: (string | { id: string; label?: string })[],
    getTagProps: any,
    fieldName: string
  ) => {
    const options = fields.find((f) => f.fieldName === fieldName)?.valueMap;
    return value.map((option, index) => {
      const id =
        typeof option === 'object' && option !== null && 'id' in option
          ? (option as { id: string }).id
          : String(option);
      const resolvedLabel = options?.find((o: any) => o.id === id)?.label;
      const label =
        resolvedLabel ??
        (typeof option === 'object' && option !== null && 'label' in option
          ? (option as { label?: string }).label
          : null) ??
        id;
      return (
        <Chip
          variant="outlined"
          label={label}
          {...getTagProps({ index })}
        />
      );
    });
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleCancel}
        fullWidth
        maxWidth="lg"
        aria-labelledby="add-record-dialog-title"
        aria-describedby="add-record-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Advanced Search</DialogTitle>
        <DialogContent>
          {searchFilters.map((searchRow, idx) => (
            <Grid container spacing={2} key={`${searchRow.fieldName}-${idx}`}>
              <Grid size={4}>
                <ComboBoxField
                  handleInputChange={(e) => handleInputChange(idx, e)}
                  labelColumn="label"
                  idColumn="fieldName"
                  label="Column"
                  name="fieldName"
                  style={classes.tabComboBox}
                  value={searchRow.fieldName}
                  values={fields}
                />
              </Grid>
              <Grid size={3}>
                <ComboBoxField
                  handleInputChange={(e) => handleInputChange(idx, e)}
                  labelColumn="label"
                  idColumn="value"
                  label="Operator"
                  name="operator"
                  style={classes.tabComboBox}
                  value={searchRow.operator}
                  values={[
                    { label: 'Contains', value: 'contains' },
                    { label: 'Equals', value: 'equals' },
                    { label: 'Starts With', value: 'startsWith' },
                    { label: 'Ends With', value: 'endsWith' },
                    { label: 'Is Any Of', value: 'isAnyOf' },
                  ]}
                />
              </Grid>
              <Grid size={4}>
                {searchRow.operator !== 'isAnyOf' &&
                  schema?.fields &&
                  schema?.fields[searchRow.fieldName]?.type !== 'enum' && (
                    <TextField
                      name="value"
                      label="Value"
                      variant="filled"
                      value={searchRow.value}
                      onChange={(e) => handleInputChange(idx, e)}
                      fullWidth
                      className={classes.textField}
                    />
                  )}
                {searchRow.operator !== 'isAnyOf' &&
                  schema?.fields &&
                  schema?.fields[searchRow.fieldName]?.type === 'enum' && (
                    <ComboBoxField
                      handleInputChange={(e) => handleInputChange(idx, e)}
                      labelColumn="label"
                      idColumn="id"
                      label="Value"
                      name="value"
                      style={classes.tabComboBox}
                      value={searchRow.value}
                      values={
                        fields.find((f) => f.fieldName === searchRow.fieldName)
                          ?.valueMap || []
                      }
                    />
                  )}
                {searchRow.operator === 'isAnyOf' &&
                  schema?.fields &&
                  schema?.fields[searchRow.fieldName]?.type !== 'enum' && (
                    <div className={classes.anyOfField}>
                      <Autocomplete
                        clearIcon={false}
                        options={[]}
                        freeSolo
                        value={searchRow.values}
                        onChange={(e, v) => handleAnyOfInputChange(idx, e, v)}
                        multiple
                        renderTags={(value, props) =>
                          value.map((option, index) => (
                            <Chip label={option} {...props({ index })} />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField label="Values" {...params} />
                        )}
                        className={classes.isAnyOfField}
                      />
                    </div>
                  )}
                {searchRow.operator === 'isAnyOf' &&
                  schema?.fields &&
                  schema?.fields[searchRow.fieldName]?.type === 'enum' && (
                    <div className={classes.anyOfField}>
                      <Autocomplete
                        multiple
                        clearIcon={false}
                        options={
                          fields.find(
                            (f) => f.fieldName === searchRow.fieldName
                          )?.valueMap || []
                        }
                        isOptionEqualToValue={(option: any, value) =>
                          option.id === value
                        }
                        value={searchRow.values.map((val) => ({
                          id: val,
                          label: val,
                        }))}
                        onChange={(e, v) => handleAnyOfInputChange(idx, e, v)}
                        renderTags={(v, p) =>
                          renderAnyOfEnumTags(v, p, searchRow.fieldName)
                        }
                        renderInput={(params) => (
                          <TextField label="Values" {...params} />
                        )}
                        className={classes.isAnyOfField}
                      />
                    </div>
                  )}
              </Grid>
              <Grid size={1}>
                <IconButton
                  onClick={() => {
                    const updatedSearchFilters = [...searchFilters];
                    updatedSearchFilters.splice(idx, 1);
                    setSearchFilters(updatedSearchFilters);
                  }}
                  className={classes.iconButton}
                  size="large"
                >
                  <Icon>delete</Icon>
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button
            onClick={() =>
              setSearchFilters([
                ...searchFilters,
                {
                  fieldName: '',
                  operator: 'contains',
                  value: '',
                  values: [],
                },
              ])
            }
          >
            Add Filter
          </Button>
        </DialogContent>
        <DialogActions sx={{ marginLeft: 2, marginRight: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={disablePagination}
                onChange={(e) => setDisablePagination(e.target.checked)}
              />
            }
            label="Disable Pagination"
          />
          <FormControlLabel
            control={
              <Switch
                checked={forceRefresh}
                onChange={(e) => setForceRefresh(e.target.checked)}
              />
            }
            label="Force Refresh"
          />
          <div style={{ flex: '1 0 0' }} />
          <Button onClick={handleCancel} color="primary">
            Close
          </Button>
          <Button
            disabled={searchFilters.some(
              (f) =>
                f.fieldName === '' || (f.value === '' && f.values.length === 0)
            )}
            onClick={handleSave}
            color="primary"
            autoFocus
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdvSearchDialog;
